// https://developer.fullstory.com/

import { ProductAnalytics } from 'derivita/ts/analytics/service/analytics';

/**
 * User vars have special naming requirements.
 * See https://help.fullstory.com/hc/en-us/articles/360020623294
 * and https://help.fullstory.com/hc/en-us/articles/360020623234#Custom%20Property%20Name%20Requirements
 */
export interface UserVars {
  [key: string]: unknown;
}

interface FullStory {
  /**
   * Returns a url to the current session.
   * Must be called after the session recording has started, e.g. after the
   * _fs_ready callback.
   * @param now If true, starts playback at the current moment
   * within the session
   */
  getCurrentSessionURL(now?: boolean): string;

  /**
   * Returns the id of the current session.
   * Must be called after the session recording has started, e.g. after the
   * _fs_ready callback.
   */
  getCurrentSession(): string;

  /**
   * restart() begins recording again after shutdown() has been called.
   */
  restart(): void;

  /**
   * Once shutdown() is invoked, FullStory will no longer receive data from
   * the user's device. When running FullStory in a web browser, recording
   * begins automatically once the page is loaded from the server; shutdown()
   * must be called each time after a page is loaded.
   * - Can be invoked immediately after the FullStory snippet is executed.
   */
  shutdown(): void;

  identify(uid: string, userVars?: UserVars): void;

  setUserVars(userVars: UserVars): void;

  log(msg: string): void;
  log(level: string, msg: string): void;
  log(level: string, msg?: string): void;

  event(name: string, properties?: UserVars): void;
  displayedRuntimeError(msgDisplayed: string): void;
}
const FS = goog.global['FS'] as FullStory | undefined;

export const fullstory = {
  /**
   * Returns a url to the current session.
   * Must be called after the session recording has started, e.g. after the
   * _fs_ready callback.
   * @param now If true, starts playback at the current moment
   * within the session
   */
  getCurrentSessionURL(now?: boolean): string {
    if (FS && FS['getCurrentSessionURL']) {
      return FS['getCurrentSessionURL'](now);
    }
    return '';
  },

  /**
   * Returns the id of the current session.
   * Must be called after the session recording has started, e.g. after the
   * _fs_ready callback.
   */
  getCurrentSession(): string {
    if (FS && FS['getCurrentSession']) {
      return FS['getCurrentSession']();
    }
    return '';
  },

  restart(): void {
    if (FS && FS['restart']) {
      FS['restart']();
    }
  },

  shutdown(): void {
    if (FS && FS['shutdown']) {
      FS['shutdown']();
    }
  },

  identify(uid: string, userVars?: UserVars): void {
    if (FS && FS['identify']) {
      FS['identify'](uid, userVars);
    }
  },

  setUserVars(userVars: UserVars): void {
    if (FS && FS['setUserVars']) {
      FS['setUserVars'](userVars);
    }
  },

  displayedRuntimeError(msgDisplayed: string): void {
    if (FS && FS['event']) {
      FS['event']('Displayed Runtime Error', {
        'msg_displayed_str': msgDisplayed,
      });
    }
  },
};

export class Fullstory implements ProductAnalytics {
  captureLog(msg: string, level?: string): void {
    if (FS && FS['log']) {
      if (level) {
        FS['log'](level, msg);
      } else {
        FS['log'](msg);
      }
    }
  }

  captureEvent(name: string, properties?: UserVars): void {
    // See https://developer.fullstory.com/destinations/v1/webhooks/list-event-types/custom-event/
    // for more information on custom events
    if (FS && FS['event']) {
      FS['event'](name, properties);
    }
  }
}
